'use client';
import React, { useState, useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import MobileTopBar from './MobileTopBar';
import MobileTabBar from './MobileTabBar';
import MobileMenuDrawer from './MobileMenuDrawer';
import { useRoleAuth } from '@/contexts/RoleAuthContext';
import { MENU_ITEMS } from './navItems';
import LicenceBanner from './LicenceBanner';

interface AppLayoutProps {
  children: React.ReactNode;
  title?: string;
  titleTh?: string;
}

const MOBILE_QUERY = '(max-width: 767px)';

export default function AppLayout({ children, title = '', titleTh }: AppLayoutProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const { roleUser, roleLoading } = useRoleAuth();
  const router = useRouter();
  const pathname = usePathname();

  // Title ownership lives here: pages may pass an explicit title, but the
  // default derives from the nav item matching the current path — so the
  // topbar always shows the right (translated) page name and never a stale
  // "Dashboard" fallback on sub-pages.
  const navItem = MENU_ITEMS.find((mi) => mi.href === pathname);
  const resolvedTitle = title || navItem?.label || 'Dashboard';
  const resolvedTitleTh = titleTh || navItem?.labelTh || resolvedTitle;

  // Server render and first client render both use the desktop shell so hydration
  // always matches; the matchMedia listener flips to mobile right after mount.
  // Dev-only: `?mobile=1` forces the mobile shell so it can be previewed on a
  // desktop browser. NODE_ENV is inlined by Next.js, so in a production build
  // `forced` is a noop and the entire override machinery is stripped as dead code.
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const forced =
      process.env.NODE_ENV !== 'production'
        ? () => {
            try {
              const params = new URLSearchParams(window.location.search);
              if (params.get('mobile') === '1') {
                // Stick across client-side navigations so previewing stays on mobile.
                sessionStorage.setItem('__glanzMobilePreview', '1');
                return true;
              }
              if (params.get('mobile') === '0') {
                sessionStorage.removeItem('__glanzMobilePreview');
                return false;
              }
              return sessionStorage.getItem('__glanzMobilePreview') === '1';
            } catch {
              return false;
            }
          }
        : () => false;
    const mq = window.matchMedia(MOBILE_QUERY);
    const onChange = () => {
      setIsMobile(mq.matches || forced());
    };
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // Never leave the full-nav drawer open when returning to desktop.
  useEffect(() => {
    if (!isMobile) setMenuOpen(false);
  }, [isMobile]);

  useEffect(() => {
    if (!roleLoading && !roleUser) {
      router.push('/login');
    }
  }, [roleUser, roleLoading, router]);

  if (roleLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <svg className="animate-spin w-8 h-8 text-primary" viewBox="0 0 24 24" fill="none">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
          </svg>
          <p className="text-sm text-muted-foreground">Loading…</p>
        </div>
      </div>
    );
  }

  if (!roleUser) return null;

  // Lang/currency state is owned by the root-layout LangCurrencyProvider;
  // shell components and page content consume it via useLangCurrency().
  if (isMobile) {
    return (
      <>
        <div className="min-h-screen bg-background">
          <MobileTopBar onMenuOpen={() => setMenuOpen(true)} />
          <MobileTabBar onMoreOpen={() => setMenuOpen(true)} />
          <MobileMenuDrawer menuOpen={menuOpen} onCloseMenu={() => setMenuOpen(false)} />
          {/* Top bar is sticky (in flow), so main needs no top padding. */}
          <main id="main-content" className="min-h-screen max-w-screen-2xl mx-auto">
            <LicenceBanner />
            <div className="px-4 pt-3 pb-24">{children}</div>
          </main>
        </div>
      </>
    );
  }

  const sidebarWidth = collapsed ? 64 : 224;

  return (
    <>
      <div className="min-h-screen bg-background">
        <Sidebar
          collapsed={collapsed}
          onCollapsedChange={setCollapsed}
          mobileOpen={false}
          onMobileClose={() => {}}
        />
        <Topbar title={resolvedTitle} titleTh={resolvedTitleTh} sidebarWidth={sidebarWidth} />
        <main
          id="main-content"
          className="pt-16 min-h-screen transition-all duration-300 md:ml-[var(--sidebar-width)]"
          style={{ '--sidebar-width': `${sidebarWidth}px` } as React.CSSProperties}
        >
          <LicenceBanner />
          <div className="p-4 md:p-6 max-w-screen-2xl mx-auto">{children}</div>
        </main>
      </div>
    </>
  );
}
